import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { Helmet } from 'react-helmet-async';
import {
  Container,
  Typography,
  Paper,
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  Alert,
  Breadcrumbs,
  Link,
  Divider,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { Icon } from '@iconify/react';
import { useSnackbar } from 'notistack';
import cloudApi from '../../app/api/cloudApi';
import profileApi from '../../app/api/profileApi';
import ClearSiteDataDialog from './sections/clear-site-data-dialog';
import RetryBulkInductionDialog from './sections/retry-bulk-induction-dialog';
import { useAvailableActions } from '../../hooks/useAvailableActions';
import { useTranslations } from '../../hooks/use-translations';
import {
  SITE_PROFILE_STATUSES_BULK_RETRY,
  DEFAULT_BULK_RETRY_INDUCTION_STATUSES,
} from '../../constants/siteProfileStatusesBulkRetry';

export default function AdministrativeSiteSettings() {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslations();
  const { canPerformProfileAdminActions } = useAvailableActions();
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clearSiteDataDialog, setClearSiteDataDialog] = useState(false);
  const [bulkRetryDialogOpen, setBulkRetryDialogOpen] = useState(false);
  const [bulkRetryStatuses, setBulkRetryStatuses] = useState(() => [...DEFAULT_BULK_RETRY_INDUCTION_STATUSES]);
  const [dashboardImagePreviewUrl, setDashboardImagePreviewUrl] = useState('');
  const [selectedDashboardImageFile, setSelectedDashboardImageFile] = useState(null);
  const [dashboardImageBusy, setDashboardImageBusy] = useState(false);
  const maxDashboardImageSizeMb = 5;
  const maxDashboardImageSizeBytes = maxDashboardImageSizeMb * 1024 * 1024;

  useEffect(() => {
    if (siteId && auth.user) {
      loadSiteData();
      loadDashboardImage();
    }
  }, [siteId, auth.user]);

  useEffect(() => () => {
    if (dashboardImagePreviewUrl) {
      URL.revokeObjectURL(dashboardImagePreviewUrl);
    }
  }, [dashboardImagePreviewUrl]);

  const loadSiteData = async () => {
    try {
      const response = await cloudApi.getSite(auth.user.access_token, siteId);
      setSite(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading site:', error);
      enqueueSnackbar('Failed to load site data', { variant: 'error' });
      setLoading(false);
    }
  };

  const setPreviewUrl = (nextUrl) => {
    setDashboardImagePreviewUrl((previousUrl) => {
      if (previousUrl) {
        URL.revokeObjectURL(previousUrl);
      }
      return nextUrl;
    });
  };

  const loadDashboardImage = async () => {
    if (!auth.user || !siteId) {
      return;
    }

    try {
      const response = await cloudApi.getSiteDashboardImage(auth.user.access_token, siteId);
      if (!response?.data) {
        setPreviewUrl('');
        return;
      }
      setPreviewUrl(URL.createObjectURL(response.data));
    } catch {
      setPreviewUrl('');
    }
  };

  const handleBulkRetryInductionOk = async () => {
    const res = await profileApi.retrySiteProfileInductionBulk(auth.user.access_token, {
      accountId: site.accountId,
      siteId: site.id,
      statuses: bulkRetryStatuses,
    });
    const enqueued = res.data?.enqueued ?? 0;
    setBulkRetryDialogOpen(false);
    enqueueSnackbar(t('sites.administrative.bulkRetrySuccess', { enqueued }), { variant: 'success' });
  };

  const handleClearSiteData = async () => {
    try {
      await cloudApi.clearSiteData(auth.user.access_token, siteId);
      enqueueSnackbar('Site data cleared successfully', { variant: 'success' });
      setClearSiteDataDialog(false);
    } catch (error) {
      console.error('Error clearing site data:', error);
      enqueueSnackbar('Failed to clear site data', { variant: 'error' });
      throw error;
    }
  };

  const handleDashboardImageSelect = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      enqueueSnackbar('Please select a valid image file', { variant: 'error' });
      return;
    }

    if (file.size > maxDashboardImageSizeBytes) {
      enqueueSnackbar(`Image must be ${maxDashboardImageSizeMb}MB or smaller`, { variant: 'error' });
      return;
    }

    setSelectedDashboardImageFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleSaveDashboardImage = async () => {
    if (!selectedDashboardImageFile) {
      enqueueSnackbar('Choose an image before saving', { variant: 'info' });
      return;
    }

    setDashboardImageBusy(true);
    try {
      await cloudApi.uploadSiteDashboardImage(auth.user.access_token, siteId, selectedDashboardImageFile);
      setSelectedDashboardImageFile(null);
      await loadDashboardImage();
      enqueueSnackbar('Dashboard image updated successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error uploading dashboard image:', error);
      enqueueSnackbar(error.response?.data || 'Failed to upload dashboard image', { variant: 'error' });
    }
    setDashboardImageBusy(false);
  };

  const handleClearDashboardImage = async () => {
    setDashboardImageBusy(true);
    try {
      await cloudApi.clearSiteDashboardImage(auth.user.access_token, siteId);
      setSelectedDashboardImageFile(null);
      setPreviewUrl('');
      enqueueSnackbar('Dashboard image removed successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error clearing dashboard image:', error);
      enqueueSnackbar(error.response?.data || 'Failed to remove dashboard image', { variant: 'error' });
    }
    setDashboardImageBusy(false);
  };

  const isAdmin = () => {
    try {
      if (!auth.user || !auth.user.access_token) {
        return false;
      }
      
      const token = auth.user.access_token;
      const payload = token.split('.')[1];
      if (!payload) return false;
      
      const decoded = JSON.parse(atob(payload));
      return decoded?.realm_access?.roles?.includes('admin') || false;
    } catch (error) {
      console.error("Error checking admin role:", error);
      return false;
    }
  };

  if (!isAdmin()) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">
          <Typography variant="h6">Access Denied</Typography>
          <Typography>You do not have permission to access this page.</Typography>
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  if (!site) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">Site not found</Alert>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>Administrative Site Settings | CloudLoq</title>
      </Helmet>

      <Container maxWidth="lg">
        <Box sx={{ mt: 4, mb: 4 }}>
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <Link
              underline="hover"
              color="inherit"
              sx={{ cursor: 'pointer' }}
              onClick={() => navigate('/sites')}
            >
              Sites
            </Link>
            <Typography color="text.primary">Administrative Settings</Typography>
          </Breadcrumbs>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Icon icon="mdi:shield-lock-outline" width="32" height="32" />
            <Typography variant="h4">Administrative Site Settings</Typography>
            <Chip label="Admin Only" color="error" size="small" />
          </Box>

          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              Warning: This page contains dangerous administrative actions
            </Typography>
            <Typography variant="body2">
              Actions performed here can have irreversible consequences. Please proceed with caution.
            </Typography>
          </Alert>

          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Site Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">Site Name:</Typography>
              <Typography variant="body1" fontWeight="medium">{site.name}</Typography>
              
              <Typography variant="body2" color="text.secondary">Site ID:</Typography>
              <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                {site.id}
              </Typography>
              
              <Typography variant="body2" color="text.secondary">Subdomain:</Typography>
              <Typography variant="body1">{site.subDomain || 'N/A'}</Typography>
              
              <Typography variant="body2" color="text.secondary">URL:</Typography>
              <Typography variant="body1">{site.url || 'N/A'}</Typography>
            </Box>
          </Paper>

          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Dashboard Image
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload the image displayed on this site's dashboard card.
            </Typography>
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' } }}>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  minHeight: 220,
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'grey.50',
                }}
              >
                {dashboardImagePreviewUrl ? (
                  <Box
                    component="img"
                    src={dashboardImagePreviewUrl}
                    alt="Site dashboard preview"
                    sx={{ width: '100%', maxHeight: 280, objectFit: 'contain' }}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No image uploaded for this site.
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<Icon icon="material-symbols:upload-file" />}
                  disabled={dashboardImageBusy}
                >
                  Choose Image
                  <input
                    hidden
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    onChange={handleDashboardImageSelect}
                  />
                </Button>
                <LoadingButton
                  variant="contained"
                  onClick={handleSaveDashboardImage}
                  loading={dashboardImageBusy}
                  disabled={dashboardImageBusy || !selectedDashboardImageFile}
                  startIcon={<Icon icon="material-symbols:save" />}
                >
                  Save Image
                </LoadingButton>
                <LoadingButton
                  variant="text"
                  color="error"
                  onClick={handleClearDashboardImage}
                  loading={dashboardImageBusy}
                  disabled={dashboardImageBusy || !dashboardImagePreviewUrl}
                  startIcon={<Icon icon="material-symbols:delete" />}
                >
                  Remove Image
                </LoadingButton>
                <Typography variant="caption" color="text.secondary">
                  Supported formats: PNG, JPG, WEBP, GIF. Maximum file size: {maxDashboardImageSizeMb}MB.
                </Typography>
              </Box>
            </Box>
          </Paper>

          {canPerformProfileAdminActions && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t('sites.administrative.bulkRetrySectionTitle')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('sites.administrative.bulkRetrySectionIntro')}
              </Typography>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="bulk-retry-statuses-label">{t('sites.administrative.bulkRetryStatusesLabel')}</InputLabel>
                <Select
                  labelId="bulk-retry-statuses-label"
                  multiple
                  value={bulkRetryStatuses}
                  onChange={(e) => {
                    const next = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                    setBulkRetryStatuses(next);
                  }}
                  input={<OutlinedInput label={t('sites.administrative.bulkRetryStatusesLabel')} />}
                  renderValue={(selected) => selected.join(', ')}
                >
                  {SITE_PROFILE_STATUSES_BULK_RETRY.map((status) => (
                    <MenuItem key={status} value={status}>
                      <Checkbox checked={bulkRetryStatuses.indexOf(status) > -1} size="small" />
                      <ListItemText primary={status} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                {t('sites.administrative.bulkRetryStatusesHint')}
              </Typography>
              <Button
                variant="contained"
                color="warning"
                startIcon={<Icon icon="mdi:restart-alert" />}
                onClick={() => {
                  if (!bulkRetryStatuses.length) {
                    enqueueSnackbar(t('sites.administrative.bulkRetrySelectStatus'), { variant: 'warning' });
                    return;
                  }
                  setBulkRetryDialogOpen(true);
                }}
              >
                {t('sites.administrative.bulkRetryQueueButton')}
              </Button>
              <RetryBulkInductionDialog
                dialogOpen={bulkRetryDialogOpen}
                okAction={handleBulkRetryInductionOk}
                cancelAction={() => setBulkRetryDialogOpen(false)}
                selectedStatuses={bulkRetryStatuses}
              />
            </Paper>
          )}

          <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
            Dangerous Operations
          </Typography>

          <Card sx={{ border: '2px solid', borderColor: 'error.main' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Icon icon="mdi:database-remove" width="40" height="40" color="error" />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" color="error" gutterBottom>
                    Clear All Site Data
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    This action will permanently delete ALL data associated with this site. This includes:
                  </Typography>
                  <Box component="ul" sx={{ mt: 1, mb: 2 }}>
                    <li>
                      <Typography variant="body2">Events and Activity Logs</Typography>
                    </li>
                    <li>
                      <Typography variant="body2">Profile Activities and State Tracking</Typography>
                    </li>
                    <li>
                      <Typography variant="body2">Profile Activity Anomalies</Typography>
                    </li>
                    <li>
                      <Typography variant="body2">Doors and Door Groups</Typography>
                    </li>
                    <li>
                      <Typography variant="body2">User Profiles</Typography>
                    </li>
                    <li>
                      <Typography variant="body2">Access Levels</Typography>
                    </li>
                  </Box>
                  <Alert severity="error" sx={{ mt: 2 }}>
                    <Typography variant="body2" fontWeight="bold">
                      ⚠️ THIS ACTION CANNOT BE UNDONE!
                    </Typography>
                    <Typography variant="body2">
                      All historical data for this site will be permanently lost.
                    </Typography>
                  </Alert>
                </Box>
              </Box>
            </CardContent>
            <CardActions sx={{ justifyContent: 'flex-end', p: 2, pt: 0 }}>
              <Button
                variant="contained"
                color="error"
                size="large"
                startIcon={<Icon icon="mdi:alert-circle" />}
                onClick={() => setClearSiteDataDialog(true)}
              >
                Clear All Site Data
              </Button>
            </CardActions>
          </Card>

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-start' }}>
            <Button
              variant="outlined"
              startIcon={<Icon icon="mdi:arrow-left" />}
              onClick={() => navigate('/sites')}
            >
              Back to Sites
            </Button>
          </Box>
        </Box>

        <ClearSiteDataDialog
          dialogOpen={clearSiteDataDialog}
          okAction={handleClearSiteData}
          cancelAction={() => setClearSiteDataDialog(false)}
          site={site}
        />
      </Container>
    </>
  );
}

